import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';

export default function ScorePredictor() {
  const { API, mode } = useApp();
  const nav = useNavigate();
  const isCyber = mode === 'cyber';
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/predictor/questions`).then(r => setQs(r.data.questions));
  }, [API]);

  const submit = async () => {
    const payload = { answers: Object.entries(ans).map(([question_id, a]) => ({ question_id, answer: a })) };
    const r = await axios.post(`${API}/predictor/submit`, payload);
    setResult(r.data);
  };

  const pick = (letter) => {
    const q = qs[idx];
    setAns(a => ({ ...a, [q.id]: letter }));
    setTimeout(() => {
      if (idx < qs.length - 1) setIdx(idx + 1);
    }, 200);
  };

  if (!qs.length) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const q = qs[idx];
  const progress = ((idx + 1) / qs.length) * 100;

  const cyberSty = isCyber;

  return (
    <div className={`min-h-screen px-6 py-10 ${isCyber ? 'bg-black text-white' : 'text-[#2C2621]'}`}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button data-testid="predictor-back" onClick={() => nav('/')} className={isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story'}>
            ← back
          </button>
          <ModeToggle />
        </div>

        <div className={isCyber ? 'font-mono-cyber text-xs text-[#00FFD1] mb-2' : 'font-body-story italic text-[#68615A] mb-2'}>
          {isCyber ? `// question ${idx + 1} of ${qs.length}` : `Consultation ${idx + 1} of ${qs.length}`}
        </div>
        <h1 className={isCyber ? 'font-orbitron text-3xl mb-8' : 'font-storybook text-4xl mb-8'}>
          {isCyber ? 'SCORE PREDICTOR' : 'The Score Oracle'}
        </h1>

        <div className={`h-2 w-full rounded-full mb-8 overflow-hidden ${isCyber ? 'bg-neutral-900' : 'bg-[#E8DCC4]'}`}>
          <motion.div animate={{ width: `${progress}%` }}
            className={isCyber ? 'h-full bg-[#00FFD1]' : 'h-full bg-[#8C1C13]'} />
        </div>

        {!result ? (
          <motion.div key={q.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={isCyber ? 'glass-cyber rounded-2xl p-8' : 'parchment rounded-2xl p-8'}>
            <div className={`text-sm mb-2 ${isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story italic text-[#68615A]'}`}>
              {q.subject} · Difficulty {q.difficulty}
            </div>
            <div className={`text-xl mb-6 ${isCyber ? 'font-orbitron' : 'font-storybook'}`}>{q.prompt}</div>
            <div className="grid gap-3">
              {Object.entries(q.choices).map(([letter, text]) => {
                const selected = ans[q.id] === letter;
                return (
                  <button key={letter} data-testid={`predictor-choice-${letter}`}
                    onClick={() => pick(letter)}
                    className={`text-left px-5 py-4 rounded-xl border-2 transition-all ${
                      selected
                        ? isCyber ? 'border-[#00FFD1] bg-[#00FFD1]/10' : 'border-[#8C1C13] bg-[#8C1C13]/10'
                        : isCyber ? 'border-neutral-800 hover:border-[#00FFD1]/40' : 'border-[#C9B99A] hover:border-[#8C1C13]/40'
                    }`}>
                    <span className={`inline-block w-7 mr-2 font-bold ${isCyber ? 'text-[#00FFD1] font-mono-cyber' : 'text-[#8C1C13] font-storybook'}`}>{letter}.</span>
                    {text}
                  </button>
                );
              })}
            </div>
            {idx === qs.length - 1 && ans[q.id] && (
              <button data-testid="predictor-submit" onClick={submit}
                className={`mt-6 px-6 py-3 rounded-xl font-bold ${isCyber ? 'bg-[#00FFD1] text-black font-orbitron tracking-widest' : 'storybook-btn'}`}>
                {isCyber ? 'COMPUTE_SCORE →' : 'Reveal My Fortune'}
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className={isCyber ? 'glass-cyber rounded-2xl p-10 text-center cyber-glow-strong' : 'parchment rounded-2xl p-10 text-center'}>
            <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] mb-3' : 'font-body-story italic text-[#68615A] mb-3'}>
              {isCyber ? '// analysis complete' : 'The Oracle has spoken'}
            </div>
            <div className={isCyber ? 'font-orbitron text-6xl text-[#00FFD1] mb-3' : 'font-storybook text-6xl text-[#8C1C13] mb-3'}>
              {result.predicted_low}–{result.predicted_high}
            </div>
            <div className={isCyber ? 'font-mono-cyber text-neutral-400' : 'font-body-story text-[#68615A]'}>
              You answered {result.score} of {result.total} correctly.
            </div>
            <div className="mt-8 flex gap-3 justify-center">
              <button data-testid="predictor-plan-cta" onClick={() => nav('/study-plan')}
                className={isCyber ? 'px-5 py-2 border border-[#00FFD1]/40 text-[#00FFD1] font-orbitron rounded-lg' : 'storybook-btn px-5 py-2 rounded-lg'}>
                {isCyber ? 'BUILD PLAN' : 'Chart My Journey'}
              </button>
              <button data-testid="predictor-restart" onClick={() => { setIdx(0); setAns({}); setResult(null); }}
                className={isCyber ? 'px-5 py-2 border border-neutral-700 text-neutral-300 rounded-lg font-orbitron' : 'px-5 py-2 border-2 border-[#C9B99A] rounded-lg font-body-story'}>
                Try Again
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
